import { call, put } from 'redux-saga/effects';
import { SheetCommandType } from '../../../types/commands/sheetCommands/sheetCommands';
import { SheetManagementSet } from '../../../types/sheet/draft';
import { handleSheetOperations } from './rootActions';
import { executeSheetsCommand } from '../../../utils/commands/execute/executeSheetsCommand';
import { startAddNewWindow } from '../../../../windows/redux/windowsSlice';
import { ApplicationType } from '../../../../windows/types';
import { SheetGroupsCommandType } from '../../../types/commands/sheetGroupsCommands/sheetGroupCommands';
import { updateStateFromSheetsManagementSet } from '../../sheetsManagerSlice';
import { HistoryActionType } from '../../../types';
import sessionStorageSingleton from '../../../../core/configs/sessionStorageSingleton';
import { sheetSharedStorageWorker } from '../../../storage';
import { UserKeyStore } from '../../../../core/database/schemas';
import {
  SheetSharedStorageWorkerMessageType,
  StartSyncSheetManagementSet,
} from '../../../storage/types';
import {
  adjustSheetViewports,
  generatePendingQueueKey,
  generateSheetsDataSet,
  updateAndStoreDraftedSheets,
} from './rootHelpers';
import { showErrorToast } from '../../../../core/utils/view/toast';

export function* handleSheetOperationsSaga({
  payload,
}: ReturnType<typeof handleSheetOperations>) {
  const set: SheetManagementSet = yield generateSheetsDataSet();

  const t1 = performance.now();
  try {
    yield executeSheetsCommand(set, payload);
    yield call(startUpdateSheetsState, set);
  } catch (err) {
    sessionStorageSingleton.release();

    console.log(err);
    if (err instanceof Error && err.message) {
      showErrorToast(err.message);
    }
  }

  const t2 = performance.now();
  console.log('Total Time Takes', t2 - t1);
}

export function* startUpdateSheetsState(
  set: SheetManagementSet,
  historyAction?: HistoryActionType
) {
  console.log('set', set);

  // Replace Draft
  updateAndStoreDraftedSheets(set);

  let groupId: string | undefined;
  let sheetId: string | undefined;
  set.present.creationCommands?.forEach((command) => {
    if (command.type === SheetGroupsCommandType.CreateGroup) {
      groupId = command.group.id;
    } else if (command.type === SheetCommandType.CreateSheet) {
      sheetId = command.sheet.id;
    }
  });
  if (!groupId) groupId = set.updatedGroups.keys().next().value;
  if (!sheetId) sheetId = set.updatedSheets.keys().next().value;

  console.log('activeSheetSet==', set.updatedSheets.get(sheetId ?? ''));

  if (groupId) {
    yield call(startAddNewWindow, {
      appType: ApplicationType.Sheet,
      groupId,
      sheetId: sheetId ?? '',
    });
  }

  // Calculate Viewport
  yield call(adjustSheetViewports, set);

  // If Main Sheet Id exists in temporary but not in updatedsheets then add it to updatedSheets
  // This will be useful when we run sheet creation command & there is no other command
  // In this case the sheet will only be in temporary sheet therefore switch it to updatedSheet to update
  if (sheetId) {
    if (!set.updatedSheets.has(sheetId)) {
      const temporaryMainSheet = set.temporarySheets.get(sheetId);
      if (temporaryMainSheet)
        set.updatedSheets.set(sheetId, temporaryMainSheet);
    }
  }

  set.draftedSheets.clear();
  set.temporarySheets.clear();
  set.staticSheets = {};

  yield put(
    updateStateFromSheetsManagementSet({
      id: set.id,
      deletedGroups: set.deletedGroups,
      deletedSheets: set.deletedSheets,
      updatedGroups: set.updatedGroups,
      updatedSheets: set.updatedSheets,
      updatedEntityIdToNameMap: set.updatedEntityIdToNameMap,
      updatedSheetViews: set.updatedSheetViews,
      present: set.present,
      past: set.past,
      historyAction,
    })
  );

  sessionStorageSingleton.release();

  const userId: string = sessionStorageSingleton.getUserId();

  const masterFeKey: CryptoKey = yield sessionStorageSingleton.getFeKey(
    UserKeyStore.MasterFeKey
  );
  const userFeKey: CryptoKey = yield sessionStorageSingleton.getFeKey(
    UserKeyStore.UserFeKey
  );

  const { keyWithoutSequence, sheetsCommands, sheetFeKeys } =
    yield generatePendingQueueKey(userId, set.present);

  console.log('====sheetsCommands====', sheetsCommands);

  const managementSetForDB: StartSyncSheetManagementSet = {
    type: SheetSharedStorageWorkerMessageType.SyncAndSaveSheetSet,
    sheetsCommands,
    userId,
    masterFeKey,
    userFeKey,
    keyWithoutSequence,
    sheetFeKeys,
  };

  sheetSharedStorageWorker.port.postMessage(managementSetForDB);
}
